import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { MENU_PAPER_MAX_HEIGHT_PX } from '../../../../components/NRAutocomplete/useNRAutocompleteSelect';
import { JIRA_ASSIGNEE_UNASSIGNED_LABEL, JIRA_UNASSIGNED_ASSIGNEE_ID } from './jiraIntegration.constants';

const NO_ASSIGNEES_FOUND = 'No assignees found';

function NRAssigneeAvatar({ avatarUrl, name, isUnassigned = false }) {
  if (isUnassigned) {
    return <AccountCircleOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />;
  }

  return (
    <Avatar src={avatarUrl || undefined} alt={name} sx={{ width: 20, height: 20, fontSize: 10 }}>
      {name?.charAt(0)?.toUpperCase()}
    </Avatar>
  );
}

function NRAssigneeOptionLabel({ avatarUrl, name, isUnassigned = false }) {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <NRAssigneeAvatar avatarUrl={avatarUrl} name={name} isUnassigned={isUnassigned} />
      <Typography component="span" variant="body2">
        {name}
      </Typography>
    </Box>
  );
}

function NRJiraAssigneeSelect({ id, label, value, onChange, users = [], disabled = false, required = false, sxProps }) {
  const theme = useTheme();
  const options = useMemo(
    () => [
      {
        value: JIRA_UNASSIGNED_ASSIGNEE_ID,
        name: JIRA_ASSIGNEE_UNASSIGNED_LABEL,
        isUnassigned: true
      },
      ...users.map(user => ({
        value: user.accountId,
        name: user.displayName,
        avatarUrl: user.avatarUrl
      }))
    ],
    [users]
  );

  const selectedOption = useMemo(() => options.find(option => option.value === value) || options[0], [options, value]);

  return (
    <Autocomplete
      id={id}
      options={options}
      value={selectedOption}
      disabled={disabled}
      disableClearable
      onChange={(_, nextOption) => onChange(nextOption?.value || JIRA_UNASSIGNED_ASSIGNEE_ID)}
      getOptionLabel={option => option.name || ''}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={option.value ?? key} {...optionProps}>
            <NRAssigneeOptionLabel avatarUrl={option.avatarUrl} name={option.name} isUnassigned={option.isUnassigned} />
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          color="secondary"
          size="medium"
          sx={sxProps}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedOption ? (
              <Box display="flex" alignItems="center" pl={0.5}>
                <NRAssigneeAvatar avatarUrl={selectedOption.avatarUrl} name={selectedOption.name} isUnassigned={selectedOption.isUnassigned} />
              </Box>
            ) : null
          }}
        />
      )}
      ListboxProps={{ sx: { maxHeight: MENU_PAPER_MAX_HEIGHT_PX } }}
      componentsProps={{
        popper: {
          sx: {
            boxShadow: theme.shadows[3],
            borderRadius: 1.5
          }
        }
      }}
      noOptionsText={NO_ASSIGNEES_FOUND}
      handleHomeEndKeys
      fullWidth
    />
  );
}

export default NRJiraAssigneeSelect;
